import { atom } from "jotai";
import React, { createContext, ReactNode } from "react";
import { animationsAtom, smetaStore } from "../../../atoms";
import { uuid } from "../../TokenProcessing/TokenProcessing";
import { makeCallBacks } from "../NotWorkArea(Side&Head)/Header/Header";
import { Button, DlgRes } from "./ProjectX";

type ModalWindowsButtons =
  | "Кнопка$ОК"
  | "Кнопка$Отмена"
  | "Кнопка$Выбрать"
  | "Кнопка$Да"
  | "Кнопка$Нет"
  | "Кнопка$Прервать"
  | "Кнопка$Повторить"
  | "Кнопка$Пропустить"
  | "Кнопка$Для всех"
  | "Кнопка$Нет для всех"
  | "Кнопка$Да для всех";

export const buttonLabels: Record<number, ModalWindowsButtons> = {
  [Button.Ok]: "Кнопка$Выбрать",
  [Button.Cancel]: "Кнопка$Отмена",
  [Button.Yes]: "Кнопка$Да",
  [Button.No]: "Кнопка$Нет",
  [Button.Abort]: "Кнопка$Прервать",
  [Button.Retry]: "Кнопка$Повторить",
  [Button.Ignore]: "Кнопка$Пропустить",
  [Button.All]: "Кнопка$Для всех",
  [Button.NoToAll]: "Кнопка$Нет для всех",
  [Button.YesToAll]: "Кнопка$Да для всех",
};

export type ModalWindowsButtonsObject = {
  Buttons?: number;
  jsonData?: { [key in ModalWindowsButtons]?: string };
  Params?: { Buttons: number };
  disabledButtons?: { [key in Button]?: boolean };
  hiddenButtons?: { [key in Button]?: boolean };
};

interface OpenWindowsProps {
  id?: string;
  /** CSS стили модального окна. В них можно задать ограничения максимального/минимального размера окна.*/
  style?: React.CSSProperties;
  /** Скрытие кнопки "Во весь экран" и блокировка события двойного клика на шапке модального окна.*/
  blockMaximize?: boolean;
  /**Функция, которая будет вызвана при закрытии модального окна через кнопку "Отмена"(если она не скрыта) или через кнопку в шапке документа. */
  onClose?: (value: string) => Promise<void> | void;
  /** Функция, которая будет вызвана при нажатии стандартной кнопки "Выбрать" если она не скрыта.*/
  onSelect?: (value: string) => Promise<void> | void;
  /** Заголовок модального окна.*/
  Title?: string;
  /**Скрытие стандартных кнопок. */
  hiddenButton?: boolean;
  /**Число от которого будет считаться zIndex у элемента заднего фона модального окна. */
  zIndex?: number;
  /** Блокировка изменений размеров модального окна.*/
  blockResize?: boolean;
  Buttons?: number | ModalWindowsButtonsObject;
  /**Блокировка сохранения позиционирования и размеров модального окна */
  blockSaveLocation?: boolean;
  /**Установка фокуса на модальное окно */
  setFocus?: boolean;
  /**Элемент, на который установится фокус после закрытия модального окна */
  elementToFocusAfterModal?: HTMLElement;
  /**Убрать блокировку заднего фона */
  disableBlockBackground?: boolean;
  /**Функция проверки можно ли закрыть модальное окно */
  closingQuery?: (value: string) => Promise<boolean> | boolean;
  /**Отключение вызова callback которые находятся в атоме unloadCallbacksAtom */
  callBacks?: boolean;
  /** Скрытия кнопки "Свернуть/Развернуть" */
  blockCollapse?: boolean;
}

export interface ModalWindowsProps extends OpenWindowsProps {
  closing?: boolean;
  children?: ReactNode;
  hasBlockBackground?: boolean;
}

export type ContextButtons = {
  [key in Button]?: {
    label?: string;
    onClick?: () => void | Promise<void>;
    disabled?: boolean;
    hidden?: boolean;
  };
};

type ModalPortalArray = {
  datasetId: string;
  content: ReactNode;
  props: ModalWindowsProps;
}[];

// eslint-disable-next-line @typescript-eslint/naming-convention
export const SmetaModalsIdContext = createContext("");
// eslint-disable-next-line @typescript-eslint/naming-convention
export const SmetaModalsButtonsContext = createContext<
  React.Dispatch<React.SetStateAction<ContextButtons>> | undefined
>(undefined);
export const smetaModalsAtom = atom<ModalPortalArray>([]);
let timer: NodeJS.Timeout | null = null;

const refreshMinimizedWindowsPosition = (allMinimizedWindows: HTMLDivElement[]) => {
  if (allMinimizedWindows.length) {
    allMinimizedWindows.sort((a, b) => a.getBoundingClientRect().right - b.getBoundingClientRect().right);
    allMinimizedWindows[0].style.left = "5px";
    let lastPlacedWindow = allMinimizedWindows[0];
    for (let i = 1; i < allMinimizedWindows.length; i++) {
      const currentWindow = allMinimizedWindows[i];
      const previousWindow = allMinimizedWindows[i - 1];
      const newLeft = parseFloat(previousWindow.style.left) + previousWindow.offsetWidth + 10;
      if (newLeft + currentWindow.offsetWidth < document.documentElement.clientWidth) {
        currentWindow.style.left = newLeft + "px";
        currentWindow.style.top = previousWindow.offsetTop + "px";
        lastPlacedWindow = currentWindow;
      } else {
        currentWindow.style.left = parseFloat(lastPlacedWindow.style.left) + "px";
        currentWindow.style.top = lastPlacedWindow.offsetTop + "px";
      }
    }
  }
};

const openModal = (content: ReactNode, props: OpenWindowsProps = {}) => {
  const datasetId = uuid();
  const newModal = {
    datasetId,
    content: content,
    props: props,
  };
  const prevModals = smetaStore.get(smetaModalsAtom);
  smetaStore.set(smetaModalsAtom, [...prevModals, newModal]);
  return datasetId;
};

const closeModal = async (idModalWindow?: string, executeOnClose = false) => {
  const prevModals = smetaStore.get(smetaModalsAtom);
  if (prevModals.length === 0) return;
  const closingFunction = async () => {
    const lastModal = prevModals[prevModals.length - 1];
    const closedModal = idModalWindow
      ? (prevModals.find((modal) => modal.datasetId === idModalWindow) ?? lastModal)
      : lastModal;
    const proprClosedModal = closedModal.props;
    if (executeOnClose) {
      const afterCallBacks = proprClosedModal.callBacks === true ? await makeCallBacks() : true;
      if (!afterCallBacks) return;
      const shouldClose = proprClosedModal.closingQuery
        ? await proprClosedModal.closingQuery(DlgRes.Cancel.toString())
        : true;
      if (!shouldClose) return;
      proprClosedModal.onClose?.("");
    }
    return smetaStore.set(
      smetaModalsAtom,
      prevModals.filter((modal) => modal !== closedModal),
    );
  };

  if (!smetaStore.get(animationsAtom)) return await closingFunction();
  if (!timer) {
    const newModals = [...prevModals];
    const lastModal = newModals[newModals.length - 1];
    const closedModal = idModalWindow
      ? (newModals.find((modal) => modal.datasetId === idModalWindow) ?? lastModal)
      : lastModal;
    closedModal.props.closing = true;
    smetaStore.set(smetaModalsAtom, newModals);
    timer = setTimeout(async () => {
      await closingFunction();
      timer = null;
    }, 226);
  }
};

const updateModalProps = (updatedProps: OpenWindowsProps, idModalWindow?: string) => {
  const prevModals = smetaStore.get(smetaModalsAtom);
  if (prevModals.length === 0) return;
  const newModals = [...prevModals];
  const lastModal = newModals[newModals.length - 1];
  const changedModal = idModalWindow
    ? (newModals.find((modal) => modal.datasetId === idModalWindow) ?? lastModal)
    : lastModal;
  changedModal.props = { ...changedModal.props, ...updatedProps };
  smetaStore.set(smetaModalsAtom, newModals);
};

export const smetaModals = {
  open: openModal,
  close: closeModal,
  refreshMinimizedWindowsPosition,
  updateModalProps,
};
